import { Box, Text, HStack } from "@chakra-ui/layout";
import { ReactNode } from "react";

import { G1Button } from "@components/G1Button";
import { $g1Color } from "@/theme/G1Style";
import { G1Icon } from "@/utils/icons";

import { Step } from "./RegisterTypes";
import { StepSummary } from "./StepSummary";

interface StepConfig {
    key: Step;
    label: string;
    element: ReactNode;
}

interface RegisterStepperProps {
    steps: StepConfig[];
    stepOrder: Step[];
    currentStep: Step;
    completedSteps: Set<Step>;
    validateStep: (step: Step) => boolean;
    onStepClick: (step: Step) => void;
    onNextStep: (current: Step, next: Step) => void;
    // StepSummary props
    summaryProps: Omit<React.ComponentProps<typeof StepSummary>, "step">;
}

export default function RegisterStepper(props: RegisterStepperProps) {
    const {
    steps,
    stepOrder,
    currentStep,
    completedSteps,
    validateStep,
    onStepClick,
    onNextStep,
    summaryProps,
} = props;

    return (
        <>
            {steps.map(({ key, label, element }, index) => {
                const isCompleted = completedSteps.has(key);
                const isActive = currentStep === key;
                const isLocked = !isActive && !isCompleted;

                return (
                    <Box
                        key={key}
                        width="100%"
                        borderWidth="2px"
                        borderRadius="lg"
                        borderColor={isCompleted ? "green.300" : isActive ? "blue.400" : "gray.200"}
                        bg={$g1Color("connection.form.bg")}
                        color={$g1Color("connection.form.write")}
                        opacity={isLocked ? 0.5 : 1}
                        transition="all 0.3s"
                    >
                        <HStack
                            px={6} py={4}
                            cursor={isCompleted ? "pointer" : "default"}
                            onClick={() => isCompleted && onStepClick(key)}
                            justify="space-between"
                        >
                            <HStack spacing={3}>
                                <Box
                                    borderRadius="full" w={6} h={6}
                                    display="flex" alignItems="center" justifyContent="center"
                                    bg={isCompleted ? "green.400" : isActive ? "blue.400" : "gray.300"}
                                    color="white" fontSize="xs" fontWeight="bold"
                                >
                                    {isCompleted ? <G1Icon.Check /> : index + 1}
                                </Box>
                                <Text
                                    fontWeight="semibold" fontSize="2xl"
                                    color={isActive ? "blue.500" : isCompleted ? "green.500" : "gray.400"}
                                >
                                    {label}
                                </Text>
                            </HStack>
                            {isCompleted && !isActive && <G1Icon.Down />}
                        </HStack>

                        {isCompleted && !isActive && (
                            <StepSummary step={key} {...summaryProps} />
                        )}

                        {isActive && (
                            <Box px={6} pb={6}>
                                {element}
                                {key !== "confirmation" && (
                                    <HStack width="100%" justifyContent="flex-end" mt={4}>
                                        <G1Button
                                            component="connection"
                                            variant="ok"
                                            title="Continuer"
                                            isDisabled={!validateStep(key)}
                                            onClick={() => onNextStep(key, stepOrder[index + 1])}
                                        />
                                    </HStack>
                                )}
                            </Box>
                        )}
                    </Box>
                );
            })}
        </>
    );
}